import { NavLink, Link, useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  Briefcase,
  ArrowLeftRight,
  PieChart,
  Settings,
  ChevronLeft,
  ChevronRight,
  Calculator,
  Calendar,
  Building2,
  Shield,
  Upload,
  Search,
  Globe,
  Users,
  Crown,
  Package,
  Kanban,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAppStore } from '@/store'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import type { LucideIcon } from 'lucide-react'
const primary: [string, LucideIcon, string][] = [
  ['/dashboard', LayoutDashboard, 'Мой капитал'],
  ['/portfolios', Briefcase, 'Портфели и счета'],
  ['/import', Upload, 'Загрузить отчёт'],
  ['/lab', Calculator, 'Проверить доходность'],
  ['/public', Globe, 'Публичные портфели'],
]
const secondary: [string, LucideIcon, string][] = [
  ['/exchange', PieChart, 'Биржевой обзор'],
  ['/transactions', ArrowLeftRight, 'Операции'],
  ['/realestate', Building2, 'Недвижимость'],
  ['/other-assets', Package, 'Бизнес и другие активы'],
  ['/captables', PieChart, 'Доли и опционы'],
  ['/dividends', Calendar, 'Календарь выплат'],
  ['/tax', Calculator, 'Налоговый расчёт'],
  ['/screener', Search, 'Поиск бумаг'],
  ['/pipelines', Kanban, 'Воронка сделок'],
  ['/community', Users, 'Сообщество'],
]
export function Sidebar() {
  const { sidebarOpen, toggleSidebar, setSidebarOpen } = useAppStore()
  const { user } = useAuth()
  const { pathname } = useLocation()
  const closeMobile = () => {
    if (window.innerWidth < 1024) setSidebarOpen(false)
  }
  const item = ([path, Icon, name]: [string, LucideIcon, string]) => (
    <NavLink
      key={path}
      to={path}
      onClick={closeMobile}
      title={name}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:text-primary hover:bg-accent',
          isActive && 'bg-accent text-primary font-medium',
          !sidebarOpen && 'justify-center'
        )
      }
    >
      <Icon aria-hidden size={18} className="shrink-0" />
      <span className={sidebarOpen ? '' : 'sr-only'}>{name}</span>
    </NavLink>
  )
  return (
    <>
      {sidebarOpen && (
        <button
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/30 lg:hidden"
        />
      )}
      <aside
        aria-label="Навигация по разделам"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-card border-r transition-all duration-200',
          sidebarOpen ? 'w-64' : 'w-20 max-lg:hidden'
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b">
          {sidebarOpen && (
            <Link
              to="/dashboard"
              onClick={closeMobile}
              className="wealth-logo"
              style={{ fontSize: 22 }}
            >
              <span>э</span>Эльвирка
            </Link>
          )}
          <Button
            variant="ghost"
            size="icon"
            aria-label={sidebarOpen ? 'Свернуть меню' : 'Развернуть меню'}
            onClick={toggleSidebar}
            className="ml-auto"
          >
            {sidebarOpen ? (
              <>
                <ChevronLeft className="hidden lg:block h-5 w-5" />
                <X className="lg:hidden h-5 w-5" />
              </>
            ) : (
              <ChevronRight size={20} />
            )}
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto p-2 space-y-1">
          {primary.map(item)}
          <details
            open={
              !sidebarOpen || secondary.some(([p]) => pathname.startsWith(p))
            }
            className="pt-4"
          >
            <summary
              className={cn(
                'cursor-pointer text-xs text-muted-foreground px-3 mb-2',
                !sidebarOpen && 'sr-only'
              )}
            >
              Все инструменты
            </summary>
            {secondary.map(item)}
          </details>
          <div className="pt-4 border-t mt-4">
            {item(['/settings', Settings, 'Настройки'])}
            {item(['/pricing', Crown, 'Тарифы'])}
            {user?.is_admin && item(['/admin', Shield, 'Администрирование'])}
          </div>
        </nav>
        {sidebarOpen && (
          <div className="p-4 border-t text-xs text-muted-foreground">
            <Link
              to="/demo"
              onClick={closeMobile}
              className="underline underline-offset-4"
            >
              Открыть пример портфеля ↗
            </Link>
          </div>
        )}
      </aside>
    </>
  )
}
